import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'store/hooks';

import { STATUSES, USER_ROLES } from 'constants/common';

import { userSelector } from 'store/auth/selectors';

import * as api from 'api/methods';

import {
  fetchPublishedCoursesForLessonRatings,
  getEmptyAggregatedData,
  getCurrentLessonRatingsCourseOption,
  prepareLessonRatingsCourseOptions,
  type LessonRatingsCourse,
} from './LessonRatingsCard.helpers';

import LessonRatingsCard from './LessonRatingsCard';

const LessonRatingsCardContainer = (props: any) => {
  const user = useAppSelector(userSelector);

  const isRootAdmin = user.role === USER_ROLES.ROOT_ADMIN;

  const [currentCourse, setCurrentCourse] =
    useState<LessonRatingsCourse | null>(null);
  const [courses, setCourses] = useState<LessonRatingsCourse[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [aggregatedData, setAggregatedData] = useState(getEmptyAggregatedData());
  const [loadingRatings, setLoadingRatings] = useState(false);
  const [expandedModuleIds, setExpandedModuleIds] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    let isCancelled = false;

    const loadCourses = async () => {
      setLoadingCourses(true);

      try {
        const fetchedCourses = await fetchPublishedCoursesForLessonRatings({
          isRootAdmin,
          schoolId: user.schoolId,
        });

        if (!isCancelled) {
          setCourses(fetchedCourses);
        }
      } catch (error) {
        console.warn(error);

        if (!isCancelled) {
          setCourses([]);
        }
      }

      if (!isCancelled) {
        setLoadingCourses(false);
      }
    };

    loadCourses();

    return () => {
      isCancelled = true;
    };
  }, [isRootAdmin, user.schoolId]);

  const publishedCourses = useMemo(
    () =>
      courses.filter(
        (course) => course.status === STATUSES.PUBLISHED,
      ),
    [courses],
  );

  useEffect(() => {
    if (publishedCourses.length) {
      setCurrentCourse(publishedCourses[0]);
    } else {
      setCurrentCourse(null);
    }
  }, [publishedCourses]);

  useEffect(() => {
    if (!currentCourse) {
      setAggregatedData(getEmptyAggregatedData());
      return;
    }

    const fetchRatingsStats = async () => {
      setLoadingRatings(true);

      try {
        const params = {
          courseId: currentCourse.id,
        };

        const response = isRootAdmin
          ? await api.getLessonRatingsStats(params)
          : await api.getSchoolLessonRatingsStats({
              schoolId: user.schoolId,
              params,
            });

        setAggregatedData(response ?? getEmptyAggregatedData());
      } catch (error) {
        console.warn(error);
        setAggregatedData(getEmptyAggregatedData());
      }

      setLoadingRatings(false);
    };

    fetchRatingsStats();
  }, [currentCourse, isRootAdmin, user.schoolId]);

  const handleCourseChange = (option: any) => {
    const nextCourse = publishedCourses.find(
      (course) => course.id === option.value,
    );

    setCurrentCourse(nextCourse ?? null);
    setExpandedModuleIds({});
  };

  const handleModuleToggle = (moduleId: string) => {
    setExpandedModuleIds((prevState: Record<string, boolean>) => ({
      ...prevState,
      [moduleId]: !prevState[moduleId],
    }));
  };

  return (
    <LessonRatingsCard
      {...props}
      loadingCourses={loadingCourses}
      loadingRatings={loadingRatings}
      hasCourses={publishedCourses.length > 0}
      courseOptions={prepareLessonRatingsCourseOptions(publishedCourses)}
      currentCourseOption={getCurrentLessonRatingsCourseOption(currentCourse)}
      aggregatedData={aggregatedData}
      expandedModuleIds={expandedModuleIds}
      onCourseChange={handleCourseChange}
      onModuleToggle={handleModuleToggle}
    />
  );
};

export default React.memo(LessonRatingsCardContainer) as any;
